
import Page from '@/Page.vue';
import { Component, Prop } from 'vue-property-decorator';
import {
    AuditModel,
    EscalationGroupTypeEnum,
    ImplementationStateEnum,
    MeasureModel,
    MeasureVerificationUserTypeEnum,
    PermissionTypeEnum,
    ProcessTypeEnum,
    TenantUserModel,
} from '@/libs/Api';
import WorkTaskDialogFiles from '../components/WorkTaskDialogFiles.vue';
import WorkTaskEscalation from '@/libs/work-tasks/components/WorkTaskEscalation.vue';
import { Guid } from '@/libs/common/functions/guid';
import moment from 'moment/moment';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import { formatDate } from '@/libs/common/functions/format-date';

@Component({
    components: {
        WorkTaskEscalation,
        WorkTaskDialogFiles,
    },
})
export default class WorkTaskFindingItemMeasure extends Page {
    /* Component props */
    @Prop() measure!: MeasureModel;
    @Prop() audit!: AuditModel;
    @Prop({ default: false }) readonly!: boolean;
    @Prop({ default: true }) canDelete!: boolean;
    protected originalImplementationState: ImplementationStateEnum = ImplementationStateEnum.New;
    protected tenantUser = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    protected invalidDialogVerificationUsers = false;
    protected measureVerificationUsers: string[] = [];

    protected item: MeasureModel | null = null;
    protected PermissionTypeEnum: typeof PermissionTypeEnum = PermissionTypeEnum;
    protected ImplementationStateEnum: typeof ImplementationStateEnum = ImplementationStateEnum;
    protected EscalationGroupTypeEnum: typeof EscalationGroupTypeEnum = EscalationGroupTypeEnum;
    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;

    @Prop({ default: false }) useMeasureType!: boolean;

    protected get canChangeState() {
        if (!this.audit) {
            return false;
        }
        if (!this.audit.category) {
            return false;
        }
        if (!this.audit.category.enablePerformWhenCreatingMeasure) {
            return false;
        }
        if (
            this.originalImplementationState == ImplementationStateEnum.Verified &&
            !this.audit.category.enableVerificationWhenCreatingMeasure
        ) {
            return false;
        }
        return true;
    }

    get isMobile(): boolean {
        return this.$vuetify.breakpoint.width < 576;
    }

    mounted() {
        this.item = { ...this.measure };
        this.originalImplementationState = this.item.implementationState ?? ImplementationStateEnum.New;
        this.measureVerificationUsers = this.getVerificationTenantUserList(this.item);
    }

    public getMeasure(): MeasureModel | null {
        if (
            this.audit &&
            this.audit.category &&
            this.item &&
            this.audit.category.processType == ProcessTypeEnum.Audit &&
            this.item.isPerfomedWhenCreated &&
            !this.item.isVerifiedWhenCreated
        ) {
            this.setVerificationTenantUserList(this.item, this.measureVerificationUsers);
        }
        return { ...this.item };
    }

    public validate(): boolean {
        if (
            this.audit &&
            this.audit.category &&
            this.item &&
            this.audit.category.processType == ProcessTypeEnum.Audit &&
            this.item.isPerfomedWhenCreated &&
            !this.item.isVerifiedWhenCreated
        ) {
            this.invalidDialogVerificationUsers = (this.measureVerificationUsers ?? []).length == 0;
            return !this.invalidDialogVerificationUsers;
        }
        return true;
    }

    protected removeMeasure() {
        this.$emit('remove');
    }

    protected getImplementationTenantUserList(measure: MeasureModel): string[] {
        return measure?.measureImplementationUsers?.map((x) => x.tenantUserId!) ?? [];
    }

    protected setImplementationTenantUserList(measure: MeasureModel, tenantUsers: string[]) {
        if (!measure) {
            return;
        }
        if (!measure.measureImplementationUsers) {
            measure.measureImplementationUsers = [];
        }
        const existingTenantUsers = measure.measureImplementationUsers.map((x) => x.tenantUserId!);
        const tenantUsersToAdd = tenantUsers.filter((x) => !existingTenantUsers.contains(x));
        const tenantUsersToDelete = existingTenantUsers.filter((x) => !tenantUsers.contains(x));
        for (const tenantUser of tenantUsersToAdd) {
            measure.measureImplementationUsers.push({
                id: Guid.EmptyGuid(),
                tenantUserId: tenantUser,
            });
        }
        for (const tenantUser of tenantUsersToDelete) {
            const implementationUser = measure.measureImplementationUsers.find((x) => x.tenantUserId! == tenantUser)!;
            measure.measureImplementationUsers.splice(measure.measureImplementationUsers.indexOf(implementationUser), 1);
        }
    }

    protected getVerificationTenantUserList(measure: MeasureModel): string[] {
        return measure?.measureVerificationUsers?.map((x) => x.tenantUserId!) ?? [];
    }

    protected setVerificationTenantUserList(measure: MeasureModel, tenantUsers: string[]) {
        if (!measure) {
            return;
        }
        if (!measure.measureVerificationUsers) {
            measure.measureVerificationUsers = [];
        }
        const existingTenantUsers = measure.measureVerificationUsers.map((x) => x.tenantUserId!);
        const tenantUsersToAdd = tenantUsers.filter((x) => !existingTenantUsers.contains(x));
        const tenantUsersToDelete = existingTenantUsers.filter((x) => !tenantUsers.contains(x));
        for (const tenantUser of tenantUsersToAdd) {
            measure.measureVerificationUsers.push({
                id: Guid.EmptyGuid(),
                tenantUserId: tenantUser,
            });
        }
        for (const tenantUser of tenantUsersToDelete) {
            const implementationUser = measure.measureVerificationUsers.find((x) => x.tenantUserId! == tenantUser)!;
            measure.measureVerificationUsers.splice(measure.measureVerificationUsers.indexOf(implementationUser), 1);
        }
    }

    protected onImplementationStateChange() {
        if (this.item?.implementationState == ImplementationStateEnum.New) {
            this.item!.isPerfomedWhenCreated = false;
            this.item!.perfomedWhenCreatedAt = null;
            this.item!.perfomedWhenCreatedByUserId = null;
            this.item!.isVerifiedWhenCreated = false;
            this.item!.verifiedWhenCreatedAt = null;
            this.item!.verifiedWhenCreatedByUserId = null;
        } else if (this.item?.implementationState == ImplementationStateEnum.WaitingForVerification) {
            this.item!.isPerfomedWhenCreated = true;
            this.item!.perfomedWhenCreatedAt = moment().format('YYYY-MM-DDTHH:mm:ss');
            this.item!.perfomedWhenCreatedByUserId = this.tenantUser.id;
            this.item!.isVerifiedWhenCreated = false;
            this.item!.verifiedWhenCreatedAt = null;
            this.item!.verifiedWhenCreatedByUserId = null;
        } else if (this.item?.implementationState == ImplementationStateEnum.Verified) {
            if (!this.item!.isPerfomedWhenCreated) {
                this.item!.isPerfomedWhenCreated = true;
                this.item!.perfomedWhenCreatedAt = moment().format('YYYY-MM-DDTHH:mm:ss');
                this.item!.perfomedWhenCreatedByUserId = this.tenantUser.id;
            }
            this.item!.isVerifiedWhenCreated = true;
            this.item!.verifiedWhenCreatedAt = moment().format('YYYY-MM-DDTHH:mm:ss');
            this.item!.verifiedWhenCreatedByUserId = this.tenantUser.id;
        }
        if (this.item?.implementationState == ImplementationStateEnum.WaitingForVerification) {
            this.item!.implementationTime = moment().format('YYYY-MM-DDTHH:mm:ss');
            this.item!.implementerUserId = this.tenantUser.id;
            this.item.measureImplementationUsers = [
                {
                    tenantUserId: this.tenantUser.id,
                },
            ];
        }
        if (this.item?.implementationState == ImplementationStateEnum.Verified) {
            if (!this.item!.implementerUserId) {
                this.item!.implementationTime = moment().format('YYYY-MM-DDTHH:mm:ss');
                this.item!.implementerUserId = this.tenantUser.id;
                this.item.measureImplementationUsers = [
                    {
                        tenantUserId: this.tenantUser.id,
                    },
                ];
            }
            this.item!.verificationUserId = this.tenantUser.id;
            this.item.measureVerificationUsers = [
                {
                    tenantUserId: this.tenantUser.id,
                },
            ];
        }
    }
}
